import { computed } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';

/**
 * Composable for managing conversation labels
 * @returns {Object} An object containing methods and computed properties for conversation labels
 */
export function useConversationLabels() {
  const store = useStore();
  const getters = useStoreGetters();

  /**
   * The currently selected chat
   * @type {import('vue').ComputedRef<Object>}
   */
  const currentChat = computed(() => getters.getSelectedChat.value);

  /**
   * The ID of the current conversation
   * @type {import('vue').ComputedRef<number|null>}
   */
  const conversationId = computed(() => currentChat.value?.id);

  /**
   * All labels available for the account
   * @type {import('vue').ComputedRef<Array>}
   */
  const accountLabels = computed(() => getters['labels/getLabels'].value);

  /**
   * Labels currently saved to the conversation
   * @type {import('vue').ComputedRef<Array>}
   */
  const savedLabels = computed(() => {
    return store.getters['conversationLabels/getConversationLabels'](
      conversationId.value
    );
  });

  /**
   * Normalizes a label title so a conversation tag and an account label are
   * treated as the same even when they differ only by case or spacing/hyphen/
   * underscore (e.g. tag "Kaki Busuk" / "kaki busuk" == account label
   * "kakibusuk"). Keeps the sidebar in sync with the reply-box tag bar.
   * @param {string} title
   * @returns {string}
   */
  const normalizeLabelKey = title =>
    String(title || '')
      .toLowerCase()
      .replace(/[\s_-]+/g, '');

  /**
   * Labels currently active on the conversation.
   * Every saved conversation label is returned (same source the reply-box tag
   * bar renders). A saved tag is matched to a registered account label
   * (case/spacing-insensitive) for its color/description; unmatched tags get a
   * neutral fallback chip instead of being silently hidden.
   * @type {import('vue').ComputedRef<Array>}
   */
  const activeLabels = computed(() => {
    const saved = Array.isArray(savedLabels.value) ? savedLabels.value : [];
    const account = Array.isArray(accountLabels.value)
      ? accountLabels.value
      : [];

    const accountByKey = new Map();
    account.forEach(label => {
      const key = normalizeLabelKey(label.title);
      if (key && !accountByKey.has(key)) {
        accountByKey.set(key, label);
      }
    });

    const seen = new Set();
    const result = [];
    saved.forEach((title, index) => {
      const key = normalizeLabelKey(title);
      if (!key || seen.has(key)) return;
      seen.add(key);
      const matched = accountByKey.get(key);
      result.push(
        matched || {
          id: `fallback-${index}-${title}`,
          title,
          description: '',
          color: '#64748b',
        }
      );
    });
    return result;
  });

  /**
   * Labels available but not active on the conversation (normalized compare so a
   * tag already applied under a different spelling is not offered again).
   * @type {import('vue').ComputedRef<Array>}
   */
  const inactiveLabels = computed(() => {
    const savedKeys = new Set(
      (Array.isArray(savedLabels.value) ? savedLabels.value : []).map(
        normalizeLabelKey
      )
    );
    return (
      Array.isArray(accountLabels.value) ? accountLabels.value : []
    ).filter(({ title }) => !savedKeys.has(normalizeLabelKey(title)));
  });

  /**
   * Updates the labels for the current conversation
   * @param {string[]} selectedLabels - Array of label titles to be set for the conversation
   * @returns {Promise<void>}
   */
  const onUpdateLabels = async selectedLabels => {
    await store.dispatch('conversationLabels/update', {
      conversationId: conversationId.value,
      labels: selectedLabels,
    });
  };

  /**
   * Adds a label to the current conversation without dropping any existing tag
   * (including tags that have no matching account label).
   * @param {Object} value - The label object to be added
   * @param {string} value.title - The title of the label to be added
   */
  const addLabelToConversation = value => {
    const saved = Array.isArray(savedLabels.value)
      ? [...savedLabels.value]
      : [];
    const key = normalizeLabelKey(value?.title);
    if (key && !saved.some(title => normalizeLabelKey(title) === key)) {
      saved.push(value.title);
    }
    onUpdateLabels(saved);
  };

  /**
   * Removes a label from the current conversation, preserving all other tags.
   * @param {string} value - The title of the label to be removed
   */
  const removeLabelFromConversation = value => {
    const key = normalizeLabelKey(value);
    const saved = (
      Array.isArray(savedLabels.value) ? savedLabels.value : []
    ).filter(title => normalizeLabelKey(title) !== key);
    onUpdateLabels(saved);
  };

  return {
    accountLabels,
    savedLabels,
    activeLabels,
    inactiveLabels,
    addLabelToConversation,
    removeLabelFromConversation,
    onUpdateLabels,
  };
}
